import { useState, useRef, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  CloudArrowUp, PenNib, SignOut, Buildings, CircleNotch, Sparkle,
  SquaresFour, ChatCircle, Camera, Files, UserCircle, Receipt,
  CalendarPlus, Bank, Briefcase, Robot, ShieldCheck, Lifebuoy,
  CheckCircle, ChatCircleText,
} from "@phosphor-icons/react";

// Portal building blocks — top-level widgets, overlays, and sidebars kept alongside Portal.jsx.
import { OnboardingWizard } from "../components/portal/OnboardingWizard";
import { SnapDocument } from "../components/portal/SnapDocument";
import { Consent7216Gate } from "../components/portal/Consent7216Gate";
import { DocPreviewDialog } from "../components/DocPreview";
import { PortalSidebar } from "../components/portal/PortalSidebar";
import { ServiceDrawer } from "../components/portal/ServiceDrawer";
import { InternationalComplianceDrawer } from "../components/portal/InternationalCompliance";
import { Exchange1031Drawer } from "../components/portal/Exchange1031";
import { PortfolioDashboard } from "../components/portal/PortfolioDashboard";
import { AssistantChat } from "../components/portal/AssistantChat";
import { FinalFlow } from "../components/portal/FinalFlow";
import ServiceCatalogDialog from "../components/portal/ServiceCatalogDialog";
import ServiceWorkspaceView from "../components/portal/ServiceWorkspaceView";
import SupportPanel from "../components/portal/SupportPanel";
import { PnlEngine } from "../components/portal/PnlEngine";
import { AIAutomationStatus } from "../components/portal/DashboardWidgets";
import { AdvisorySection } from "../components/portal/PortalSections";
import { ScenarioCalculator } from "../components/portal/ScenarioCalculator";

// Extracted tab bodies (refactored Feb 2026 to slim Portal.jsx from ~800 → ~350 lines).
import OverviewTab from "../components/portal/tabs/OverviewTab";
import TaxTab from "../components/portal/tabs/TaxTab";
import DocumentsTab from "../components/portal/tabs/DocumentsTab";
import BillingTab from "../components/portal/tabs/BillingTab";

export default function Portal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [entityId, setEntityIdState] = useState(() => {
    const saved = localStorage.getItem("portal_entity_id");
    if (saved) api.defaults.headers.common["X-Entity-ID"] = saved;
    return saved;
  });

  const { data: portfolio } = useQuery({ queryKey: ["portfolio"], queryFn: () => api.get("/portfolio").then((r) => r.data) });
  const entities = portfolio?.entities || [];

  const selectEntity = (id) => {
    if (id) { api.defaults.headers.common["X-Entity-ID"] = id; localStorage.setItem("portal_entity_id", id); }
    else { delete api.defaults.headers.common["X-Entity-ID"]; localStorage.removeItem("portal_entity_id"); }
    setEntityIdState(id);
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "portfolio" });
    qc.invalidateQueries({ queryKey: ["portfolio"] });
  };

  useEffect(() => {
    if (!portfolio) return;
    const ids = entities.map((e) => e.id);
    if (entityId && !ids.includes(entityId)) selectEntity(null);
    else if (!entityId && ids.length === 1) selectEntity(ids[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolio]);

  const currentEntity = entities.find((e) => e.id === entityId);
  const activeYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-zinc-50 text-slate-900" data-testid="portal-page">
      <header className="bg-white border-b border-zinc-200 px-8 py-4 flex items-center justify-between sticky top-0 z-30 shadow-sm">
        <div className="flex items-center gap-2.5">
          <img src="/artha-logo.png" alt="Artha" className="w-9 h-9 object-contain" />
          <div>
            <p className="font-heading font-bold text-sm text-slate-900" style={{ fontFamily: "'Cardo', Georgia, serif" }}>
              Client Portal{currentEntity ? ` · ${currentEntity.name}` : ""}
            </p>
            <p className="text-[10px] text-zinc-500">Artha Business Solutions · Secure · MFA enforced</p>
          </div>
          <span data-testid="header-year-badge" className="ml-3 hidden sm:inline-flex items-center gap-1 text-[10px] font-mono font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Tax Year {activeYear}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {portfolio && entityId && (
            <button
              data-testid="header-quick-upload-btn"
              title="Quick upload to your active engagement"
              onClick={() => window.dispatchEvent(new CustomEvent("portal:quick-upload"))}
              className="hidden md:flex items-center gap-1 text-[11px] text-slate-700 border border-zinc-200 rounded-md px-2.5 py-1.5 hover:bg-slate-50 hover:border-slate-900 hover:shadow-sm transition-all duration-150">
              <CloudArrowUp size={13} /> Quick upload
            </button>
          )}
          <a
            href="mailto:cpa@arthataxes.com"
            data-testid="header-cpa-avatar"
            title="Your dedicated CPA — Partner"
            className="hidden md:flex items-center gap-2 rounded-full pl-1 pr-2.5 py-1 border border-zinc-200 hover:border-slate-900 hover:shadow-sm transition-all duration-150">
            <span className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 text-white text-[10px] font-semibold flex items-center justify-center">CP</span>
            <span className="text-[11px] text-slate-700">Your CPA</span>
          </a>
          {entityId && entities.length > 1 && (
            <button data-testid="back-to-portfolio-btn" onClick={() => selectEntity(null)} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-slate-900 transition-colors duration-150">
              <SquaresFour size={14} /> Portfolio
            </button>
          )}
          <span className="text-sm text-slate-700 hidden sm:inline">{user?.name}</span>
          <button data-testid="portal-logout-btn" onClick={async () => { await logout(); navigate("/login"); }} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-slate-900 transition-colors duration-150"><SignOut size={14} /> Sign out</button>
        </div>
      </header>

      {!portfolio ? (
        <p className="text-sm text-zinc-400 p-8 flex items-center gap-2"><CircleNotch size={16} className="animate-spin" /> Loading your portfolio…</p>
      ) : !entityId ? (
        <PortfolioDashboard portfolio={portfolio} onSelect={selectEntity} />
      ) : (
        <EntityShell key={entityId} user={user} entities={entities} entityId={entityId} onSelectEntity={selectEntity} />
      )}
    </div>
  );
}

function EntityShell({ user, entities, entityId, onSelectEntity }) {
  const { data } = useQuery({
    queryKey: ["portal"],
    queryFn: () => api.get("/portal/overview").then((r) => r.data),
    refetchInterval: (q) => (q.state.data?.documents || []).some((d) => d.status === "processing") ? 3000 : false,
  });
  const [drawerService, setDrawerService] = useState(null);
  const [showCatalog, setShowCatalog] = useState(false);
  const [activeWorkspaceEngagement, setActiveWorkspaceEngagement] = useState(null);

  const drawerEngagement = useMemo(() => {
    if (!drawerService) return null;
    return (data?.engagements || []).find((e) => e.id === drawerService.engagement_id) || null;
  }, [drawerService, data]);

  return (
    <div className="flex">
      <PortalSidebar
        entities={entities}
        entityId={entityId}
        onSelectEntity={onSelectEntity}
        engagements={data?.engagements || []}
        onOpenService={(svc) => {
          if (svc?.engagement_id) {
            setActiveWorkspaceEngagement(svc.engagement_id);
          } else {
            setDrawerService(svc);
          }
        }}
        onOpenCatalog={() => setShowCatalog(true)}
      />
      <div className="flex-1 min-w-0">
        <EntityView
          user={user} entities={entities} data={data}
          onOpenService={setDrawerService}
          onOpenCatalog={() => setShowCatalog(true)}
          activeWorkspaceEngagement={activeWorkspaceEngagement}
          setActiveWorkspaceEngagement={setActiveWorkspaceEngagement}
        />
      </div>
      <ServiceDrawer
        service={drawerService}
        engagement={drawerEngagement}
        cpa={data?.client?.assigned_cpa}
        onOpenChange={setDrawerService}
        onMessageFirm={() => {
          const el = document.querySelector('[data-testid="portal-tab-overview"]');
          el?.click();
          setTimeout(() => document.querySelector('[data-testid="portal-message-input"]')?.focus(), 200);
        }}
      />
      <ServiceCatalogDialog open={showCatalog} onOpenChange={setShowCatalog} entityId={entityId} />
    </div>
  );
}

function EntityView({ user, entities, data, onOpenService, onOpenCatalog, activeWorkspaceEngagement, setActiveWorkspaceEngagement }) {
  const qc = useQueryClient();
  const [msg, setMsg] = useState("");
  const [msgDocTag, setMsgDocTag] = useState(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [snapOpen, setSnapOpen] = useState(false);
  const [finalEng, setFinalEng] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [tab, setTab] = useState("overview");
  const [docCategory, setDocCategory] = useState("all");
  const [assistantOpen, setAssistantOpen] = useState(true);
  const [docsStep, setDocsStep] = useState(1);
  const [intlOpen, setIntlOpen] = useState(false);
  const [exchOpen, setExchOpen] = useState(false);
  const inputRef = useRef();

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("pnl_session_id") || p.get("pnl_cancel")) setTab("accounting");
  }, []);

  const { data: extras } = useQuery({ queryKey: ["portal-extras"], queryFn: () => api.get("/portal/extras").then((r) => r.data) });
  const { data: messages } = useQuery({ queryKey: ["portal-messages"], queryFn: () => api.get("/messages").then((r) => r.data), refetchInterval: 10000 });
  const { data: unread } = useQuery({ queryKey: ["portal-unread"], queryFn: () => api.get("/messages/unread-count").then((r) => r.data), refetchInterval: 10000 });
  const { data: consent } = useQuery({ queryKey: ["consent-7216"], queryFn: () => api.get("/portal/consent").then((r) => r.data) });
  const { data: tabOrderPref } = useQuery({ queryKey: ["portal-tab-order"], queryFn: () => api.get("/portal/tab-order").then((r) => r.data), staleTime: 60000 });
  const consentOk = !!consent?.signed;

  useEffect(() => {
    if (tab === "overview" && (unread?.unread || 0) > 0) {
      api.post("/messages/mark-read").then(() => qc.invalidateQueries({ queryKey: ["portal-unread"] }));
    }
  }, [tab, unread?.unread, messages?.length, qc]);

  const requireConsent = () => {
    toast.error("Please e-sign the § 7216 consent first — it's the green card above your checklist");
    document.querySelector('[data-testid="consent-7216-gate"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ["portal"] });
    qc.invalidateQueries({ queryKey: ["portal-extras"] });
    qc.invalidateQueries({ queryKey: ["portfolio"] });
  };

  const upload = useMutation({
    mutationFn: ({ file, engId, docType }) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("engagement_id", engId);
      if (docType) fd.append("checklist_doc_type", docType);
      return api.post("/documents/upload", fd);
    },
    onSuccess: () => { toast.success("Uploaded — AI is checking quality & filing it"); refreshAll(); },
    onError: (e) => toast.error(errText(e)),
  });

  const acceptAnyway = useMutation({
    mutationFn: (id) => api.post(`/documents/${id}/accept-anyway`),
    onSuccess: () => { toast.success("Accepted — routed for manual staff review"); refreshAll(); },
    onError: (e) => toast.error(errText(e)),
  });

  const moveDoc = useMutation({
    mutationFn: ({ id, target }) => api.post(`/documents/${id}/move`, { target_client_id: target }),
    onSuccess: () => { toast.success("Moved to the correct entity"); refreshAll(); },
    onError: (e) => toast.error(errText(e)),
  });

  const markNa = useMutation({
    mutationFn: ({ engId, docType }) => api.post("/portal/checklist/na", { engagement_id: engId, doc_type: docType }),
    onSuccess: () => { toast.success("Marked N/A — reminders stopped for this item"); refreshAll(); },
    onError: (e) => toast.error(errText(e)),
  });

  const sendMsg = useMutation({
    mutationFn: () => {
      const body = msgDocTag ? `Re: ${msgDocTag.filename}\n\n${msg}` : msg;
      return api.post("/messages", { client_id: user.client_id, body });
    },
    onSuccess: () => { setMsg(""); setMsgDocTag(null); qc.invalidateQueries({ queryKey: ["portal-messages"] }); },
    onError: (e) => toast.error(errText(e)),
  });

  // Header quick-upload opens the picker for the first active engagement.
  useEffect(() => {
    const handler = () => {
      if (!consentOk) return requireConsent();
      const cl = (data?.checklists || [])[0];
      if (!cl) { toast.info("No active engagement yet — your CPA will assign one."); return; }
      setUploadTarget({ engId: cl.engagement_id, docType: null });
      inputRef.current?.click();
    };
    window.addEventListener("portal:quick-upload", handler);
    return () => window.removeEventListener("portal:quick-upload", handler);
  }, [consentOk, data?.checklists]); // eslint-disable-line react-hooks/exhaustive-deps

  const pendingEnvelopes = (data?.envelopes || []).filter((e) => e.status === "pending");

  // Route TodoPanel row actions to the correct tab + prefill upload target
  const onTodoAction = (t) => {
    if (t.kind === "upload") {
      setTab("documents");
      setTimeout(() => document.getElementById("portal-checklist")?.scrollIntoView({ behavior: "smooth" }), 80);
    } else if (t.kind === "sign") {
      setTab("billing");
      if (pendingEnvelopes[0]) setFinalEng(pendingEnvelopes[0].engagement_id);
    } else if (t.kind === "pay") {
      setTab("billing");
    } else {
      setTab("overview");
      setTimeout(() => document.querySelector('[data-testid="portal-message-input"]')?.focus(), 200);
    }
  };

  const missingDocs = (data?.checklists || []).reduce((n, cl) => n + cl.items.filter((i) => !i.received).length, 0);
  const unpaidInvoices = (extras?.invoices || []).filter((i) => i.status !== "paid").length;
  const inServiceWorkspace = !!activeWorkspaceEngagement;

  // Derive which service categories are ACTIVE on this entity so we hide tabs
  // that have no work behind them (e.g. don't show "Accounting & Financials"
  // for a client who only has a 1065 tax engagement). Legacy engagements have
  // no service_category — infer "tax" from service_type (starts with 1040/1065/1120/1041/990).
  const activeCategories = useMemo(() => {
    const cats = new Set();
    (data?.engagements || []).forEach((e) => {
      if (e.service_category) { cats.add(e.service_category); return; }
      const st = (e.service_type || "").toLowerCase();
      if (/^(1040|1041|1065|1120|990|1040-nr)/.test(st) || /\btax\b/.test(st) || /schedule/.test(st)) {
        cats.add("tax");
      }
    });
    return cats;
  }, [data?.engagements]);

  const TABS_FULL = [
    { id: "overview", label: "Dashboard", icon: SquaresFour, badge: (extras?.todos?.length || 0) + pendingEnvelopes.length + (unread?.unread || 0) },
    { id: "tax", label: "Tax & Filings", icon: Receipt, badge: 0 },
    { id: "accounting", label: "Accounting & Financials", icon: Bank, badge: 0 },
    { id: "advisory", label: "Advisory & CFO", icon: Briefcase, badge: 0 },
    { id: "ai", label: "AI & Automation", icon: Robot, badge: 0 },
    { id: "documents", label: "Documents & Vault", icon: Files, badge: missingDocs },
    { id: "billing", label: "Billing & Engagements", icon: PenNib, badge: pendingEnvelopes.length + unpaidInvoices },
    { id: "messages", label: "Communication & Messages", icon: ChatCircle, badge: unread?.unread || 0 },
    { id: "support", label: "Support & Requests", icon: Lifebuoy, badge: 0 },
  ];
  // Tabs that are always shown regardless of which services the client has.
  // Category-scoped tabs (tax / accounting / advisory / ai) are shown only when
  // there's at least one active engagement in that category on this entity.
  const CORE_TAB_IDS = new Set(["overview", "documents", "billing", "messages", "support"]);
  const CATEGORY_TAB_IDS = new Set(["tax", "accounting", "advisory", "ai"]);
  const filtered = inServiceWorkspace
    ? TABS_FULL.filter((t) => ["overview", "documents", "billing", "messages"].includes(t.id))
    : TABS_FULL.filter((t) => CORE_TAB_IDS.has(t.id) || (CATEGORY_TAB_IDS.has(t.id) && activeCategories.has(t.id)));

  // Apply the firm-set per-client tab order preference (falls back to default
  // when the client has no override). Unknown / newer tab ids are appended in
  // their default position, so schema drift never hides a tab.
  const order = tabOrderPref?.tab_order || tabOrderPref?.default || [];
  const orderIndex = (id) => {
    const i = order.indexOf(id);
    return i === -1 ? 999 + TABS_FULL.findIndex((t) => t.id === id) : i;
  };
  const TABS = [...filtered].sort((a, b) => orderIndex(a.id) - orderIndex(b.id));

  // If the previously-active tab got hidden (e.g. entity switched from one with
  // accounting engagements to one with only tax), fall back to overview.
  useEffect(() => {
    if (!TABS.some((t) => t.id === tab)) setTab("overview");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [TABS.map((t) => t.id).join(",")]);

  return (
    <div className="max-w-6xl mx-auto p-4 pb-24 md:p-8 md:pb-8 space-y-5 fade-up">
      <OnboardingWizard checklists={data?.checklists} />
      <Consent7216Gate />

      <div className="sticky top-0 z-20 -mx-4 px-4 md:-mx-8 md:px-8 bg-zinc-50/95 backdrop-blur">
        <nav className="flex gap-0.5 overflow-x-auto border-b border-zinc-200" data-testid="portal-tabs" aria-label="Portal sections">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`portal-tab-${t.id}`} onClick={() => setTab(t.id)} aria-selected={tab === t.id}
              className={`flex items-center gap-1.5 whitespace-nowrap px-3 py-2.5 text-xs font-medium border-b-2 -mb-px transition-colors duration-150 ${tab === t.id ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}>
              <t.icon size={15} /> {t.label}
              {t.badge > 0 && <span className={`text-[9px] rounded-full px-1.5 py-0.5 leading-none ${tab === t.id ? "bg-zinc-900 text-white" : "bg-zinc-200 text-zinc-600"}`} data-testid={`portal-tab-badge-${t.id}`}>{t.badge}</span>}
            </button>
          ))}
          <a href="https://calendly.com/my-cpa/30" target="_blank" rel="noreferrer" data-testid="portal-schedule-call-btn"
            className="ml-auto self-center flex items-center gap-1.5 whitespace-nowrap shrink-0 px-3 py-1.5 my-1 text-xs font-medium text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md hover:bg-emerald-100 transition-colors duration-150">
            <CalendarPlus size={14} /> Schedule a call
          </a>
        </nav>
      </div>

      {tab === "overview" && (
        <OverviewTab
          data={data} extras={extras} messages={messages} unread={unread}
          pendingEnvelopes={pendingEnvelopes} onTodoAction={onTodoAction}
          setFinalEng={setFinalEng} setIntlOpen={setIntlOpen} setExchOpen={setExchOpen}
          msg={msg} setMsg={setMsg} msgDocTag={msgDocTag} setMsgDocTag={setMsgDocTag} sendMsg={sendMsg}
        />
      )}

      {tab === "tax" && <TaxTab data={data} extras={extras} onOpenService={onOpenService} />}

      {tab === "accounting" && (
        <div className="space-y-6" data-testid="portal-panel-accounting">
          <PnlEngine />
        </div>
      )}

      {tab === "advisory" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="portal-panel-advisory">
          <AdvisorySection advisory={extras?.advisory} />
          <ScenarioCalculator />
        </div>
      )}

      {tab === "ai" && (
        <div className={`grid grid-cols-1 ${assistantOpen ? "lg:grid-cols-2" : "lg:grid-cols-1"} gap-6`} data-testid="portal-panel-ai">
          <div className="space-y-6">
            <AIAutomationStatus documents={data?.documents} />
            <div className="rounded-lg border border-zinc-200 bg-white p-5 hover:shadow-md transition-shadow duration-200">
              <h3 className="text-sm font-medium text-slate-900 mb-2 flex items-center gap-2"><ShieldCheck size={14} className="text-emerald-600" /> Automation health</h3>
              <ul className="text-xs text-zinc-700 space-y-1.5">
                <li>· Auto-classification: <span className="text-emerald-700 font-medium">active</span></li>
                <li>· Bank reconciliation agent: <span className="text-emerald-700 font-medium">idle</span></li>
                <li>· AR follow-ups: <span className="text-zinc-500">not enrolled</span></li>
                <li>· Treasury sweep: <span className="text-zinc-500">not enrolled</span></li>
              </ul>
            </div>
            {!assistantOpen && (
              <button
                data-testid="assistant-expand-btn"
                onClick={() => setAssistantOpen(true)}
                className="w-full flex items-center justify-center gap-1.5 text-xs text-slate-700 border border-dashed border-zinc-300 rounded-md py-3 hover:bg-zinc-50 hover:border-slate-900 transition-colors duration-150">
                <Sparkle size={13} className="text-emerald-600" /> Open AI portal assistant
              </button>
            )}
          </div>
          {assistantOpen && (
            <div className="relative" data-testid="assistant-collapsible">
              <button
                data-testid="assistant-collapse-btn"
                title="Collapse assistant panel"
                onClick={() => setAssistantOpen(false)}
                className="absolute top-2 right-2 z-10 w-6 h-6 rounded-full bg-white border border-zinc-200 text-zinc-500 hover:text-slate-900 hover:border-slate-900 flex items-center justify-center text-xs transition-colors duration-150">
                −
              </button>
              <AssistantChat />
            </div>
          )}
        </div>
      )}

      {tab === "documents" && (
        <DocumentsTab
          data={data} entities={entities}
          docCategory={docCategory} setDocCategory={setDocCategory}
          docsStep={docsStep} setDocsStep={setDocsStep}
          consentOk={consentOk} requireConsent={requireConsent}
          uploadTarget={uploadTarget} setUploadTarget={setUploadTarget} inputRef={inputRef}
          upload={upload} acceptAnyway={acceptAnyway} moveDoc={moveDoc} markNa={markNa}
          setPreviewDoc={setPreviewDoc}
        />
      )}

      {tab === "billing" && <BillingTab data={data} extras={extras} setFinalEng={setFinalEng} />}

      {tab === "messages" && (
        <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="portal-panel-messages">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: "'Cardo', Georgia, serif" }}>
                <ChatCircle size={18} weight="fill" className="text-indigo-600" />
                Communication & Messages
              </h2>
              <p className="text-[11px] text-zinc-500 mt-0.5">A single thread with your firm — CPA updates, questions, and your replies live here.</p>
            </div>
          </div>
          <AssistantChat inline />
        </div>
      )}

      {tab === "support" && (
        <div data-testid="portal-panel-support"><SupportPanel /></div>
      )}

      {activeWorkspaceEngagement && (
        <div className="fixed inset-0 z-40 bg-white overflow-y-auto p-4 md:p-8" data-testid="service-workspace-modal">
          <div className="max-w-5xl mx-auto">
            <ServiceWorkspaceView engagementId={activeWorkspaceEngagement} onClose={() => setActiveWorkspaceEngagement(null)} />
          </div>
        </div>
      )}

      <input ref={inputRef} type="file" multiple className="hidden" data-testid="portal-file-input"
        onChange={(e) => { [...e.target.files].forEach((f) => upload.mutate({ file: f, engId: uploadTarget?.engId, docType: uploadTarget?.docType })); e.target.value = ""; }} />

      <FinalFlow engagementId={finalEng} open={!!finalEng} onOpenChange={(o) => !o && setFinalEng(null)} />
      <DocPreviewDialog doc={previewDoc} open={!!previewDoc} onOpenChange={(o) => !o && setPreviewDoc(null)} />
      <InternationalComplianceDrawer open={intlOpen} onOpenChange={setIntlOpen} />
      <Exchange1031Drawer open={exchOpen} onOpenChange={setExchOpen} />

      <div data-testid="mobile-action-bar" className="fixed bottom-0 inset-x-0 z-30 md:hidden bg-white/95 backdrop-blur border-t border-zinc-200 grid grid-cols-4">
        <button data-testid="mobile-snap-btn" disabled={!(data?.checklists || []).length} onClick={() => setSnapOpen(true)}
          className="flex flex-col items-center gap-0.5 py-2.5 text-zinc-700 active:bg-zinc-100 disabled:text-zinc-300">
          <Camera size={20} /><span className="text-[10px]">Snap</span>
        </button>
        <button data-testid="mobile-upload-btn" disabled={!(data?.checklists || []).length}
          onClick={() => { if (!consentOk) return requireConsent(); const cl = data.checklists[0]; setUploadTarget({ engId: cl.engagement_id, docType: null }); inputRef.current?.click(); }}
          className="flex flex-col items-center gap-0.5 py-2.5 text-zinc-700 active:bg-zinc-100 disabled:text-zinc-300">
          {upload.isPending ? <CircleNotch size={20} className="animate-spin" /> : <CloudArrowUp size={20} />}
          <span className="text-[10px]">Upload</span>
        </button>
        <button data-testid="mobile-checklist-btn" onClick={() => { setTab("documents"); setTimeout(() => document.getElementById("portal-checklist")?.scrollIntoView({ behavior: "smooth" }), 80); }}
          className="flex flex-col items-center gap-0.5 py-2.5 text-zinc-700 active:bg-zinc-100">
          <CheckCircle size={20} /><span className="text-[10px]">Checklist</span>
        </button>
        <button data-testid="mobile-messages-btn" onClick={() => { setTab("overview"); setTimeout(() => document.getElementById("portal-messages")?.scrollIntoView({ behavior: "smooth" }), 80); }}
          className="flex flex-col items-center gap-0.5 py-2.5 text-zinc-700 active:bg-zinc-100">
          <ChatCircleText size={20} /><span className="text-[10px]">Messages</span>
        </button>
      </div>
      <SnapDocument open={snapOpen} onOpenChange={setSnapOpen} engagementId={data?.checklists?.[0]?.engagement_id} />
    </div>
  );
}
